import { getField, updateField } from 'vuex-map-fields'
import BaseStore from './baseStore'
import { getNewId } from '@/utils/id'
import api from '../api/api'
import { generateMultipleItemOverview } from '@/store/baseOverview'

export const namespaced = true

const baseUrl = '/base_soil_types/'

const baseStore = new BaseStore(baseUrl)
const baseOverview = generateMultipleItemOverview({
  itemFilter: (soilType, revisionId) => soilType.revision === revisionId,
  duplicationCheckFunc: (soilType, revisionId) => soilType.revision === revisionId
})

const getDefaultState = () => ({
  ...structuredClone(baseStore.state),
  ...structuredClone(baseOverview.state),
  dialog: false,
  status: 'empty',
  uploadLoading: false,
  importLoading: false,
  importItems: [],
  downloadLoading: false,
  exportLoading: false,
  templateFileName: 'soil_types_template.xlsx'
})
export const state = getDefaultState()

export const mutations = {
  ...baseStore.mutations,
  ...baseOverview.mutations,
  TOGGLE_UPLOAD_LOADING(state) {
    state.uploadLoading = !state.uploadLoading
  },
  TOGGLE_IMPORT_LOADING(state) {
    state.importLoading = !state.importLoading
  },
  TOGGLE_DOWNLOAD_LOADING(state) {
    state.downloadLoading = !state.downloadLoading
  },
  TOGGLE_EXPORT_LOADING(state) {
    state.exportLoading = !state.exportLoading
  },
  ADD_IMPORT_ITEMS(state, items) {
    state.importItems = items
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
  RESET_IMPORT_STATE(state) {
    state.importItems = []
  },
  RESET_OVERVIEW_STATE(state) {
    state.itemsOverview = []
  },
  TOGGLE_WRITE_LOADING(state) {
    state.writeLoading = !state.writeLoading
  },
  updateField
}
export const actions = {
  ...baseStore.actions,
  ...baseOverview.actions,
  resetState({ commit }) {
    commit('RESET_STATE')
  },
  resetImportState({ commit }) {
    commit('RESET_IMPORT_STATE')
  },
  resetOverviewItems({ commit }) {
    commit('RESET_OVERVIEW_STATE')
  },
  async add(context) {
    const currentProjectId = context.rootGetters['project/selectedProjectId']
    const ids = []
    const id = getNewId(context.state.items, 'id', ids)
    const item = context.getters.getEmptyBaseSoilType(currentProjectId, id)
    await context.dispatch('soilType/add', id, {
      root: true
    })
    context.commit('ADD_ITEM', item)
    context.commit('ADD_COUNT', 1)
  },

  async create(context) {
    context.commit('TOGGLE_WRITE_LOADING')
    let newBaseSoilTypes = context.state.items.filter((item) => item.id <= 0)
    let newSoilTypes = []
    for (let i = 0; i < newBaseSoilTypes.length; i++) {
      let newBaseSoilType = newBaseSoilTypes[i]
      const soilType = context.rootGetters['soilType/getSoilTypeByBaseSoilTypeId'](
        newBaseSoilType.id
      )
      if (soilType) {
        newBaseSoilType.soil_type = soilType
        newSoilTypes.push(soilType)
      }
    }
    if (newBaseSoilTypes.length) {
      let response = await api.post(baseUrl, newBaseSoilTypes)
      const createdObjs = response.data['base_soil_types']
      const createdSoilTypes = response.data['soil_types']
      context.commit('REPLACE_ITEMS', { newObjs: newBaseSoilTypes, createdObjs: createdObjs })
      context.commit(
        'soilType/REPLACE_ITEMS',
        { newObjs: newSoilTypes, createdObjs: createdSoilTypes },
        { root: true }
      )
    }
    context.commit('TOGGLE_WRITE_LOADING')
  },
  async uploadFile(context, formData) {
    context.commit('TOGGLE_UPLOAD_LOADING')
    try {
      const response = await api.post(`${baseUrl}bulk_upload/`, formData)
      context.commit('ADD_IMPORT_ITEMS', response.data)
      context.commit('TOGGLE_UPLOAD_LOADING')
    } catch {
      context.commit('TOGGLE_UPLOAD_LOADING')
    }
  },

  async import(context) {
    const projectId = context.rootGetters['project/selectedProjectId']
    const selectedRevision = context.rootGetters['project/selectedSoilTypeRevision'].id
    let newObjs = context.state.importItems
    if (newObjs.length) {
      newObjs = newObjs.map((item) => {
        item['soil_type'] = { ...item['soil_type'], soil_type_revision: selectedRevision }
        const newRow = {
          ...item,
          project: projectId
        }
        return newRow
      })
      context.commit('TOGGLE_IMPORT_LOADING')
      try {
        const response = await api.post(baseUrl, newObjs)
        const createdObjs = response.data['base_soil_types']
        const createdSoilTypes = response.data['soil_types']
        context.commit('ADD_ITEMS', createdObjs)
        context.commit('ADD_COUNT', createdObjs.length)
        context.commit('soilType/ADD_ITEMS', createdSoilTypes, { root: true })
        context.commit('soilType/ADD_COUNT', createdSoilTypes.length, { root: true })
      } catch (e) {
        console.log(e)
        context.commit('TOGGLE_IMPORT_LOADING')
        throw e
      }
      context.commit('TOGGLE_IMPORT_LOADING')
    }
  },

  async downloadTemplate(context) {
    context.commit('TOGGLE_DOWNLOAD_LOADING')
    try {
      const url = baseUrl + 'template/'
      let response = await api.get(url, {
        responseType: 'blob'
      })
      context.commit('TOGGLE_DOWNLOAD_LOADING')
      return response
    } catch (e) {
      console.log(e)
      context.commit('TOGGLE_DOWNLOAD_LOADING')
      return null
    }
  },

  async exportData(context) {
    context.commit('TOGGLE_EXPORT_LOADING')
    try {
      const selectedRevisionId = context.rootGetters['project/selectedSoilTypeRevision'].id
      const url = baseUrl + 'export/' + selectedRevisionId + '/'
      let response = await api.get(url, {
        responseType: 'blob'
      })
      context.commit('TOGGLE_EXPORT_LOADING')
      return response
    } catch (e) {
      console.log(e)
      context.commit('TOGGLE_EXPORT_LOADING')
      return null
    }
  }
}

export const getters = {
  getField,
  getItemsByRevision: (state, getters, rootState) => (revisionId) => {
    const soilTypes = rootState['soilType'].items
    const baseSoilTypeIds = soilTypes
      .filter((soilType) => soilType.soil_type_revision === revisionId)
      .map((soilType) => soilType.base_soil_type)
    return state.items.filter((item) => baseSoilTypeIds.includes(item.id))
  },
  getEmptyBaseSoilType: (state) => (projectId, id) => {
    const emptyObj = {}
    Object.keys(state.options).forEach((key) => {
      emptyObj[key] = state.options[key].default !== undefined ? state.options[key].default : ''
    })

    emptyObj.project = projectId
    emptyObj.id = id
    return emptyObj
  },
  getSoilTypeById:
    ({ itemsOverview }) =>
    (id) => {
      return itemsOverview.find((item) => item.id === id) || {}
    },
  getExportFileName: (state, getters, rootState, rootGetters) => {
    const projectName = rootState['project'].currentProject?.name
    const selectedRevisionName = rootGetters['project/selectedSoilTypeRevision']?.name
    return projectName + '_' + selectedRevisionName + '_SoilTypes_export.xlsx'
  },
  getBaseSoilTypeById: (state) => (baseSoilTypeId) => {
    return state.items.find((baseSoilType) => baseSoilType.id === baseSoilTypeId) || null
  }
}
